import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box, Card, CardContent, Typography, Stack, Button, Chip, IconButton,
  Tooltip, Avatar, Alert, Snackbar,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useNavigate } from 'react-router-dom';
import { getBearerToken, putBearerToken, postBearerToken } from '../../utils/Apis';
import AppShell from '../../layouts/AppShell';
import { Constants } from '../../utils/Constants';
import './cssClasses/custom.css';

// Zoom "Connect your account" card — one-click OAuth.
const ZoomConnectCard = () => {
  const [connected, setConnected] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [zoomInfo, setZoomInfo] = useState({});
  const [toast, setToast] = useState(null); // {severity, msg}

  const loadStatus = React.useCallback(() => {
    getBearerToken('zoom/status').then((r) => {
      if (r && r.error === 0) { setConnected(!!r.connected); setZoomInfo(r); }
    });
  }, []);

  React.useEffect(() => {
    loadStatus();
    const p = new URLSearchParams(window.location.search).get('zoom');
    if (p === 'connected') setToast({ severity: 'success', msg: 'Zoom account connected successfully.' });
    else if (p === 'error') setToast({ severity: 'error', msg: 'Zoom connection failed. Please try again.' });
    if (p) window.history.replaceState({}, '', window.location.pathname);
  }, [loadStatus]);

  const connect = async () => {
    setBusy(true);
    const r = await getBearerToken('zoom/connect-url');
    if (r && r.error === 0 && r.url) { window.location.href = r.url; }
    else { setToast({ severity: 'error', msg: 'Could not start Zoom connect. Is the server configured?' }); setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Zoom? Your hosts will not be able to start meetings until you connect again. Meetings already running are not stopped.')) return;
    setBusy(true);
    await postBearerToken('zoom/disconnect', {});
    setBusy(false);
    loadStatus();
    setToast({ severity: 'info', msg: 'Zoom disconnected. New meetings cannot be started.' });
  };

  const reconnect = async () => {
    setBusy(true);
    const r = await postBearerToken('zoom/reconnect', {});
    setBusy(false);
    loadStatus();
    if (r && r.error === 0) setToast({ severity: 'success', msg: 'Zoom reconnected.' });
    else setToast({ severity: 'error', msg: (r && r.message) || 'Could not reconnect.' });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Avatar variant="rounded" sx={{ bgcolor: connected ? 'success.main' : 'primary.main', width: 48, height: 48 }}>
          <VideocamRoundedIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Zoom Account</Typography>
          <Typography variant="body2" color="text.secondary">
            {connected === null ? 'Checking connection…'
              : connected ? 'Connected — meetings run on your own Zoom account.'
              : zoomInfo.disconnected ? 'Disconnected — hosts cannot start meetings until you connect again.'
              : 'Not connected. Connect your Zoom account to host meetings.'}
          </Typography>
        </Box>
        {connected ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip icon={<CheckCircleRoundedIcon />} color="success" label="Connected" />
            <Button variant="outlined" color="inherit" disabled={busy} onClick={disconnect}>Disconnect</Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            {zoomInfo.can_reconnect && (
              <Button variant="contained" disabled={busy} onClick={reconnect}>Reconnect</Button>
            )}
            <Button variant={zoomInfo.can_reconnect ? 'outlined' : 'contained'} color={zoomInfo.can_reconnect ? 'inherit' : 'primary'}
              startIcon={<VideocamRoundedIcon />} disabled={busy || connected === null} onClick={connect}>
              Connect Zoom
            </Button>
          </Stack>
        )}
      </CardContent>
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Card>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <Card sx={{ flex: 1, minWidth: 200 }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar variant="rounded" sx={{ bgcolor: `${color}.main`, width: 52, height: 52 }}>{icon}</Avatar>
      <Box>
        <Typography variant="h3" sx={{ lineHeight: 1 }}>{value}</Typography>
        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const A_admin_list = () => {
  const navigate = useNavigate();
  // Host (SubAdmin) login: same page, limited by the server to their room's participants.
  const isHost = (localStorage.getItem(Constants.USERTYPE) || '') === 'SubAdmin';
  const [rows, setrows] = React.useState([]);
  const [rendered, setrendered] = useState(0);

  React.useEffect(() => { fetch_users(); }, [rendered]);

  const editAdmin = async (id, type) => {
    if (type === 'E') {
      navigate('/edit_sub_admin', { state: { id: id } });
    } else {
      const confirmDelete = window.confirm(isHost ? 'Delete this participant? They will no longer be able to log in.' : 'Are you sure you want to delete this user?');
      if (!confirmDelete) return;
      let current_date = new Date();
      putBearerToken('users?_id=' + id, {
        deleted_at: current_date,
        slug_name: current_date + id,
        client_id: current_date + id,
        client_secret: current_date + id,
        oauth_id: current_date + id,
        s2s_client_id: current_date + id,
        s2s_client_secret: current_date + id,
      }).then((result) => {
        if (result.error === 0) setrendered((o) => o + 1);
      });
    }
  };

  const changeStatus = (id, device_id) => {
    putBearerToken('users?_id=' + id, { device_id: device_id === '' ? 'C' : '' }).then((result) => {
      if (result.error === 0) { fetch_users(); setrendered((o) => o + 1); }
    });
  };

  const fetch_users = async () => {
    getBearerToken('users?for_admin=1').then((result) => {
      if (result.error === 0) setrows(result.data);
    });
  };

  const columns = [
    { field: 'first_name', headerName: 'First Name', flex: 1, minWidth: 130 },
    { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 120 },
    { field: 'slug_name', headerName: 'User Name', flex: 1, minWidth: 120 },
    { field: 'show_password', headerName: 'Password', flex: 1, minWidth: 110 },
    {
      field: 'type', headerName: 'Type', flex: 1, minWidth: 100,
      valueGetter: (params) => (params.row.type === 'SubAdmin' ? 'Host' : 'User'),
      renderCell: (params) => (
        <Chip size="small" label={params.value}
          color={params.value === 'Host' ? 'secondary' : 'default'}
          variant={params.value === 'Host' ? 'filled' : 'outlined'} />
      ),
    },
    { field: 'room', headerName: 'Room', flex: 1, minWidth: 120, valueGetter: (params) => params.row?.room?.name || '—' },
    {
      field: 'device', headerName: 'Device', flex: 1, minWidth: 120,
      renderCell: (params) => (params.row?.device_id
        ? <Chip size="small" color="success" variant="outlined" label={params.row.device_id} />
        : <Typography variant="body2" color="text.disabled">—</Typography>),
    },
    {
      field: 'is_active', headerName: 'Status', flex: 1, minWidth: 110,
      renderCell: (params) => (
        <Chip size="small" label={params.row.is_active ? 'Active' : 'Inactive'}
          color={params.row.is_active ? 'success' : 'error'} variant="outlined" />
      ),
    },
    {
      field: 'action', headerName: 'Actions', flex: 1.4, minWidth: 180, sortable: false, filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => editAdmin(params.row._id, 'E')}><EditRoundedIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => editAdmin(params.row._id, 'D')}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
          </Tooltip>
          {params.row.device_id !== '' && (
            <Tooltip title="Reset Device">
              <IconButton size="small" color="warning" onClick={() => changeStatus(params.row._id, params.row.device_id)}><RestartAltRoundedIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const totalUsers = rows.filter((i) => i.type === 'User').length;
  const totalHosts = rows.filter((i) => i.type === 'SubAdmin').length;
  const withDevice = rows.filter((i) => i.device_id && i.device_id !== '').length;

  return (
    <AppShell title={isHost ? 'My Room' : 'User Management'} breadcrumb={[isHost ? 'Participants' : 'Users']}>
      {/* Hosts use their admin's Zoom connection — they must never connect their own. */}
      {!isHost && <ZoomConnectCard />}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} sx={{ mb: 3 }}>
        <StatCard icon={<GroupsRoundedIcon />} label={isHost ? 'Participants' : 'Total Users'} value={totalUsers} color="primary" />
        {!isHost && <StatCard icon={<SupervisorAccountRoundedIcon />} label="Total Hosts" value={totalHosts} color="success" />}
        <StatCard icon={<DevicesRoundedIcon />} label="Devices Bound" value={withDevice} color="secondary" />
      </Stack>

      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">{isHost ? 'Participants' : 'Users'}</Typography>
        <Button sx={{ ml: 'auto' }} variant="contained" startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => navigate('/create_sub_admin')}>
          {isHost ? 'Add Participant' : 'Add User'}
        </Button>
      </Stack>

      <Box sx={{ width: '100%' }}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          getRowId={(data) => data._id}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          sx={{ '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none' } }}
        />
      </Box>
    </AppShell>
  );
};

export default A_admin_list;
